import { create } from "zustand";
import React from "react";

/**
 * Voice Selection Store
 *
 * Manages currently selected voice, language, and audio profile.
 * This replaces Redux voice selection state (voice, language, voices, selectedAudioProfile)
 *
 * Migration Strategy: Dual support with Redux during transition
 * - Zustand store is source of truth for new components
 * - Redux saga still works for backward compatibility
 * - Gradually migrate components from Redux → Zustand
 */

// Voice object structure (from API)
export interface Voice {
  code: string;
  name: string;
  engine: string;
  platform: string;
  langCode: string;
  styles: string[];
  gender: string;
  previewText: string;
  audio: string;
  qwen_pt_path?: string | null; // Pre-trained model path for Qwen emotional/cloned voices
  metadata?: any; // For Qwen emotional voices
}

// Language object structure
export interface Language {
  code: string;
  name: string;
  voices: Voice[];
  quote?: string;
}

// Audio Profile (saved voice configuration)
export interface AudioProfile {
  profile_name: string;
  lang_code: string;
  voice: string;
  engine: string;
  platform: string;
  volume?: number;
  speed?: number;
  pitch?: string;
  style?: string;
  sourceAudio?: string;
  metadata?: any;
}

interface VoiceSelectionStore {
  // Current selections
  currentVoice: Voice | null;
  currentLanguage: Language | null;
  availableVoices: Voice[]; // Filtered/available voices for current context
  selectedAudioProfile: AudioProfile | null;
  isDefaultProfile: boolean;

  // Actions - Simple setters
  setCurrentVoice: (voice: Voice | null) => void;
  setCurrentLanguage: (language: Language | null) => void;
  setAvailableVoices: (voices: Voice[]) => void;
  setSelectedAudioProfile: (profile: AudioProfile | null) => void;
  setIsDefaultProfile: (isDefault: boolean) => void;

  // Convenience actions
  selectVoiceByCode: (voiceCode: string, engine: string) => Voice | null;
  clearSelection: () => void;

  // Utility methods (don't modify state, just helpers)
  findVoiceInList: (
    voiceCode: string,
    engine: string,
    voices: Voice[]
  ) => Voice | null;
}

export const useVoiceSelectionStore = create<VoiceSelectionStore>()(
  (set, get) => ({
    // Initial state
    currentVoice: null,
    currentLanguage: null,
    availableVoices: [],
    selectedAudioProfile: null,
    isDefaultProfile: false,

    // Simple setters
    setCurrentVoice: (voice) => {
      console.log(
        "🎤 Voice Selection Store - Setting current voice:",
        voice?.name || "null"
      );
      set({ currentVoice: voice });
    },

    setCurrentLanguage: (language) => {
      console.log(
        "🌍 Voice Selection Store - Setting language:",
        language?.name || "null"
      );
      set({
        currentLanguage: language,
        // Auto-update available voices when language changes
        availableVoices: language?.voices || [],
      });
    },

    setAvailableVoices: (voices) => {
      console.log(
        "📋 Voice Selection Store - Setting available voices:",
        voices.length
      );
      set({ availableVoices: voices });
    },

    setSelectedAudioProfile: (profile) => {
      console.log(
        "🎯 Voice Selection Store - Setting audio profile:",
        profile?.profile_name || "null"
      );
      set({
        selectedAudioProfile: profile,
        // When a profile is selected, clear the individual voice selection
        currentVoice: profile ? null : get().currentVoice,
      });
    },

    setIsDefaultProfile: (isDefault) => {
      set({ isDefaultProfile: isDefault });
    },

    // Convenience actions
    selectVoiceByCode: (voiceCode, engine) => {
      const voices = get().availableVoices;
      const voice = voices.find(
        (v) => v.code === voiceCode && v.engine === engine
      );

      if (voice) {
        set({ currentVoice: voice });
        return voice;
      }

      console.warn("⚠️ Voice not found in available voices:", {
        voiceCode,
        engine,
      });
      return null;
    },

    clearSelection: () => {
      console.log("🧹 Voice Selection Store - Clearing selection");
      set({
        currentVoice: null,
        selectedAudioProfile: null,
        isDefaultProfile: false,
      });
    },

    // Utility methods
    findVoiceInList: (voiceCode, engine, voices) => {
      return (
        voices.find((v) => v.code === voiceCode && v.engine === engine) || null
      );
    },
  })
);

// ============================================================================
// Selector Hooks (for convenient component access)
// ============================================================================

/**
 * Get current voice
 * Usage: const voice = useCurrentVoice();
 */
export const useCurrentVoice = () =>
  useVoiceSelectionStore((state) => state.currentVoice);

/**
 * Get current language
 * Usage: const language = useCurrentLanguage();
 */
export const useCurrentLanguage = () =>
  useVoiceSelectionStore((state) => state.currentLanguage);

/**
 * Get available voices (filtered by current context)
 * Usage: const voices = useAvailableVoices();
 */
export const useAvailableVoices = () =>
  useVoiceSelectionStore((state) => state.availableVoices);

/**
 * Get selected audio profile
 * Usage: const profile = useSelectedAudioProfile();
 */
export const useSelectedAudioProfile = () =>
  useVoiceSelectionStore((state) => state.selectedAudioProfile);

/**
 * Get all voice selection actions
 * Usage: const { setCurrentVoice, setCurrentLanguage } = useVoiceSelectionActions();
 */
export const useVoiceSelectionActions = () =>
  useVoiceSelectionStore((state) => ({
    setCurrentVoice: state.setCurrentVoice,
    setCurrentLanguage: state.setCurrentLanguage,
    setAvailableVoices: state.setAvailableVoices,
    setSelectedAudioProfile: state.setSelectedAudioProfile,
    setIsDefaultProfile: state.setIsDefaultProfile,
    selectVoiceByCode: state.selectVoiceByCode,
    clearSelection: state.clearSelection,
  }));

// ============================================================================
// Redux Sync Helper (for dual support during migration)
// ============================================================================

/**
 * Sync Zustand store with Redux updates
 * Call this in components that still use Redux to keep Zustand in sync
 *
 * Usage in component:
 * ```
 * const reduxVoice = useSelector(state => state.voice.voice);
 * useSyncVoiceWithRedux(reduxVoice);
 * ```
 */
export const useSyncVoiceWithRedux = (reduxVoice: Voice | null) => {
  const { setCurrentVoice } = useVoiceSelectionStore();

  // Sync Redux → Zustand (one-way during migration)
  React.useEffect(() => {
    const currentVoice = useVoiceSelectionStore.getState().currentVoice;

    // Only sync if Redux voice is different (avoid loops)
    if (reduxVoice?.code !== currentVoice?.code) {
      console.log("🔄 Syncing Redux voice → Zustand:", reduxVoice?.name);
      setCurrentVoice(reduxVoice);
    }
  }, [reduxVoice, setCurrentVoice]);
};
